import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import MobileNav from './MobileNav';
import { useImpersonation } from '../context/ImpersonationContext';

function ImpersonationBanner() {
  const { isImpersonating, impersonatedAs, stopImpersonating, qaShowHidden, setQaShowHidden } = useImpersonation();
  if (!isImpersonating) return null;
  return (
    <div style={{
      background: '#fdf0f4',
      borderBottom: '1px solid #f0c4d2',
      color: '#b5556a',
      fontSize: 13,
      fontWeight: 500,
      padding: '8px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      position: 'sticky',
      top: 56,
      zIndex: 29,
      fontFamily: 'Inter, sans-serif',
    }}>
      <span>👁 Viewing as <strong>{impersonatedAs.employeeName}</strong> — read-only</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 12,
          fontWeight: 500,
          cursor: 'pointer',
          userSelect: 'none',
        }}>
          <input
            type="checkbox"
            checked={qaShowHidden}
            onChange={e => setQaShowHidden(e.target.checked)}
            style={{ accentColor: '#b5556a', cursor: 'pointer' }}
          />
          Show hidden items
        </label>
        <button
          onClick={() => { stopImpersonating(); window.location.reload(); }}
          style={{
            background: '#fff',
            border: '1px solid #f0c4d2',
            borderRadius: 6,
            color: '#b5556a',
            fontSize: 12,
            fontWeight: 500,
            padding: '4px 10px',
            cursor: 'pointer',
            fontFamily: 'Inter, sans-serif',
          }}
        >
          Exit
        </button>
      </div>
    </div>
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [query]);
  return matches;
}

export default function Layout({ session, onSignOut, activeView, onNavigate, hasModule, children }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [collapsed, setCollapsed] = useState(false);
  const sidebarWidth = isDesktop ? (collapsed ? 64 : 220) : 0;

  // Zenoti-style appointment book: entering Poppy Book auto-collapses the
  // sidebar to the icon rail so the book gets the room (the rail stays as
  // the way back); leaving restores whatever expanded/collapsed state the
  // user had before entering. Guarded on view TRANSITIONS only, so a manual
  // expand while in the Book sticks — no re-collapse until they leave and
  // re-enter. The top header bar is untouched.
  const prevViewRef = useRef(null);
  const preBookCollapsedRef = useRef(false);
  useEffect(() => {
    const prev = prevViewRef.current;
    prevViewRef.current = activeView;
    if (activeView === 'poppy_book' && prev !== 'poppy_book') {
      preBookCollapsedRef.current = collapsed;
      setCollapsed(true);
    } else if (activeView !== 'poppy_book' && prev === 'poppy_book') {
      setCollapsed(preBookCollapsedRef.current);
    }
  }, [activeView, collapsed]);

  return (
    <div style={{ minHeight: '100vh', background: '#F9FAFB' }}>
      {/* Desktop sidebar */}
      {isDesktop && session && (
        <Sidebar
          activeView={activeView}
          onNavigate={onNavigate}
          hasModule={hasModule}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed(!collapsed)}
        />
      )}

      {/* Main content area */}
      <div style={{
        marginLeft: isDesktop && session ? sidebarWidth : 0,
        transition: 'margin-left 200ms ease',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Header (always visible when logged in) */}
        {session && (
          <>
            <Header session={session} onSignOut={onSignOut} />
            <ImpersonationBanner />
          </>
        )}

        {/* Page content */}
        <main style={{
          flex: 1,
          paddingLeft: isDesktop && session ? 32 : 16,
          paddingRight: isDesktop && session ? 32 : 16,
          paddingBottom: !isDesktop && session ? 72 : 0,
        }}>
          {children}
        </main>
      </div>

      {/* Mobile bottom nav */}
      {!isDesktop && session && (
        <MobileNav
          activeView={activeView}
          onNavigate={onNavigate}
          hasModule={hasModule}
        />
      )}
    </div>
  );
}
